import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';

export const state = () => ({
    search: '',
    items: [],
    folders: [],
    source: {},
    formData: {},
    redirectTo: null,
    draggedItem: null,
    memberDrilldown: null,
    statusFormModalItem: null,
    isChangesModalOpened: false
});

export const getters = {
    items: state => state.items,
    search: state => state.search,
    source: state => state.source,
    folders: state => state.folders,
    formData: state => state.formData,
    redirectTo: state => state.redirectTo,
    draggedItem: state => state.draggedItem,
    memberDrilldown: state => state.memberDrilldown,
    statusFormModalItem: state => state.statusFormModalItem,
    isChangesModalOpened: state => state.isChangesModalOpened,
    wereAnyChanges: state => JSON.stringify(state.formData) !== JSON.stringify(state.source),

    menuItems: (state, { folders, items, search }) => {
        const categories = [
            {
                title: 'BRAND SEARCHES',
                key: 'brand',
                folders: []
            },
            {
                title: 'MY SEARCHES',
                key: 'my',
                folders: []
            },
            {
                title: 'TEAM SEARCHES',
                key: 'team',
                folders: []
            }
        ];

        const filteredItems = items.filter(item => item.name.toLowerCase().includes(search.toLowerCase()));

        const sortedItems = [...filteredItems].sort((a, b) => {
            if (b.pinned && !a.pinned) {
                return 1;
            }

            if (a.pinned && !b.pinned) {
                return -1;
            }

            return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        });

        const sortedFolders = [...folders].sort((a, b) => {
            if (b.pinned && !a.pinned) {
                return 1;
            }

            if (a.pinned && !b.pinned) {
                return -1;
            }

            return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        });

        return categories.map(item => ({
            ...item,
            folders: sortedFolders
                .filter(folder => folder.category === item.key)
                .map(folder => ({
                    ...folder,
                    items: sortedItems.filter(
                        searchItem => searchItem.category === item.key && folder.id === searchItem.folderId
                    )
                })),
            items: sortedItems.filter(searchItem => searchItem.category === item.key && !searchItem.folderId)
        }));
    }
};

export const mutations = {
    SET_ITEMS(state, items) {
        state.items = items;
    },

    SET_SOURCE(state, source) {
        state.source = source || {};
        state.formData = cloneDeep(state.source);
    },

    SET_FORM_DATA(state, value) {
        state.formData = cloneDeep({ ...state.formData, ...value });
    },

    REMOVE_FORM_DATA_PROPERTY(state, property) {
        const formData = cloneDeep(state.formData);

        delete formData[property];

        state.formData = cloneDeep(formData);
    },

    SET_SEARCH(state, search) {
        state.search = search;
    },

    SET_FOLDERS(state, folders) {
        state.folders = folders;
    },

    ADD_FOLDER(state, { folder, folderItems }) {
        state.folders.push(folder);

        state.items = state.items.map(item => ({
            ...item,
            folderId: folderItems.includes(item.id) ? folder.id : item.folderId
        }));
    },

    ADD_ITEM(state, item) {
        state.items.push(item);
    },

    UPDATE_ITEM(state, item) {
        const index = state.items.findIndex(i => i.id === item.id);

        if (~index) {
            state.items.splice(index, 1, item);
        }

        if (item.id === state.source.id) {
            state.source = cloneDeep(item);
            state.formData = cloneDeep(item);
        }
    },

    DELETE_ITEM(state, id) {
        const index = state.items.findIndex(item => item.id === id);

        if (~index) {
            state.items.splice(index, 1);
        }

        if (id === state.source.id) {
            state.source = {};
            state.formData = {};
        }
    },

    DELETE_FOLDER(state, id) {
        const index = state.folders.findIndex(item => item.id === id);

        if (~index) {
            state.folders.splice(index, 1);
        }

        state.items = state.items.map(item => ({
            ...item,
            folderId: item.folderId === id ? null : item.folderId
        }));
    },

    UPDATE_FOLDER(state, { folder, items }) {
        const index = state.folders.findIndex(f => f.id === folder.id);

        if (~index) {
            state.folders.splice(index, 1, folder);
        }

        const removedItems = state.items.filter(item => {
            return item.folderId === folder.id && !items.some(({ id }) => id === item.id);
        });

        state.items = state.items.map(item => {
            return {
                ...item,
                folderId: items.includes(item.id)
                    ? folder.id
                    : removedItems.find(removedItem => removedItem.id === item.id)
                    ? null
                    : item.folderId
            };
        });
    },

    SET_DRAGGED_ITEM(state, draggedItem) {
        state.draggedItem = draggedItem;
    },

    SET_IS_CHANGES_MODAL_OPENED(state, isChangesModalOpened) {
        state.isChangesModalOpened = isChangesModalOpened;
    },

    SET_REDIRECT_TO(state, value) {
        state.redirectTo = value;
    },

    SET_MEMBER_DRILLDOWN(state, member) {
        state.memberDrilldown = member;
    },

    SET_STATUS_FORM_MODAL_ITEM(state, item) {
        state.statusFormModalItem = item;
    }
};

export const actions = {
    async getFolders({ commit }) {
        const folders = await axios.$get('/search/folders');

        commit('SET_FOLDERS', folders);

        return folders;
    },

    async getItems({ commit }) {
        const items = await axios.$get('/search/items');

        commit('SET_ITEMS', items);

        return items;
    },

    async addFolder({ commit }, data) {
        const folder = await axios.$post('/search/folders', data);

        commit('ADD_FOLDER', { folder, folderItems: data.items });

        return folder;
    },

    async storeItem({ commit }, { name, category, folderId, logo_src, pinned }) {
        const payload = {
            name,
            category,
            folderId,
            logo_src,
            pinned
        };

        if (folderId) {
            payload.folderId;
        }

        if (logo_src) {
            payload.logo_src = logo_src;
        }

        const item = await axios.$post('/search/items', payload);

        commit('ADD_ITEM', item);

        return item;
    },

    async updateFolder({ commit }, data) {
        const folder = await axios.$put(`/search/folders/${data.id}`, data);

        commit('UPDATE_FOLDER', { folder, items: data.items });

        return folder;
    },

    async togglePin({ commit, getters: { items } }, id) {
        const { pinned } = await axios.$post(`/search/items/${id}/toggle-pin`);

        const item = items.find(i => i.id === id);

        commit('UPDATE_ITEM', { ...item, pinned });

        return pinned;
    },

    async toggleFolderPin({ commit, getters: { folders, items } }, id) {
        const { pinned } = await axios.$post(`/search/folders/${id}/toggle-pin`);

        const folder = folders.find(i => i.id === id);

        const folderItems = items.filter(item => item.folderId === folder.id);

        commit('UPDATE_FOLDER', {
            folder: { ...folder, pinned },
            items: folderItems
        });

        return pinned;
    },

    async deleteItem({ commit }, id) {
        await axios.$delete(`/search/items/${id}`);

        commit('DELETE_ITEM', id);
    },

    async deleteFolder({ commit }, id) {
        await axios.$delete(`/search/folders/${id}`);

        commit('DELETE_FOLDER', id);
    },

    setSearch({ commit }, search) {
        commit('SET_SEARCH', search);
    },

    setSource({ commit, getters: { wereAnyChanges } }, value) {
        if (wereAnyChanges) {
            commit('SET_REDIRECT_TO', value || {});
            commit('SET_IS_CHANGES_MODAL_OPENED', true);

            return;
        }

        commit('SET_SOURCE', value);
    },

    setFormDataProperty({ commit }, { property, value }) {
        commit('SET_FORM_DATA', { [property]: value });
    },

    removeFormDataProperty({ commit }, property) {
        commit('REMOVE_FORM_DATA_PROPERTY', property);
    },

    dragStarted({ commit }, item) {
        commit('SET_DRAGGED_ITEM', item);
    },

    async dragEnded({ commit, getters: { draggedItem } }, target) {
        if (draggedItem.category !== target.category || draggedItem.folderId === target.folderId) {
            return;
        }

        const item = await axios.$put(`/search/items/${draggedItem.id}`, {
            folderId: target.folderId || null
        });

        commit('UPDATE_ITEM', item);
        commit('SET_DRAGGED_ITEM', null);

        return item;
    },

    dragWordEnded({ commit, state }, wordsIndex) {
        const editedFilter = cloneDeep(state.formData.query[wordsIndex]);

        editedFilter.value = cloneDeep([...editedFilter.value, ...state.draggedItem]);

        state.formData.query.splice(wordsIndex, 1, editedFilter);

        commit('SET_DRAGGED_ITEM', null);
    },

    setModalState({ commit }, value) {
        commit('SET_IS_CHANGES_MODAL_OPENED', value);
    },

    discardChanges({ commit, getters: { source, redirectTo } }) {
        commit('SET_FORM_DATA', source);
        commit('SET_IS_CHANGES_MODAL_OPENED', false);

        if (redirectTo) {
            commit('SET_REDIRECT_TO', null);
            commit('SET_SOURCE', redirectTo);
        }
    },

    setMemberDrilldown({ commit }, member) {
        commit('SET_MEMBER_DRILLDOWN', member);
    },

    setStatusFormModalItem({ commit }, item) {
        commit('SET_STATUS_FORM_MODAL_ITEM', item);
    }
};
